import { TextAlignment } from "./textAlignment";
import { WafflesColumn } from "./wafflesColumn";

interface ColumnGroup<T> {
  alignment?: TextAlignment;
  children: Array<keyof T>;
  hideKey?: boolean;
  key: string;
  showInfoIcon?: boolean;
  testId: string;
  tooltip?: string;
}

interface ColumnConfig {
  [key: string]: WafflesColumn;
}

enum SortDirections {
  Asc = "ascending",
  Desc = "descending",
  None = "none",
}

const WafflesClassNames = {
  "headRow": "waffles-head-row",
  "table": "waffles-table",
  "tbody": "",
  "td": "waffles-cell",
  "th": "waffles-head",
  "thead": "",
  "tr": "waffles-row",
}

const wafflesKeyColumnName = "key";

const numberSort = <T>(
  data: T[],
  columnName: keyof T,
  multiplier: number,
  // this optional function parameter basically serves as an explicit type converter, or it can be used to get the right value if item is an object
  numberGenerator: (item: unknown) => string = (item: unknown) => item as string,
): T[] => {
  const nans = new Array<T>();
  let ns = new Array<T>();
  data.forEach((item) => {
    if (isNaN(parseNumber(numberGenerator(item[columnName])))) {
      nans.push(item);
    } else {
      ns.push(item);
    }
  });
  ns = ns.sort((a, b) => {
    const fa = parseNumber(numberGenerator(a[columnName]));
    const fb = parseNumber(numberGenerator(b[columnName]));
    return (fa > fb ? 1 : -1) * multiplier;
  });
  return multiplier === 1 ? nans.concat(ns) : ns.concat(nans);
};

const ratioSort = <T>(
  data: T[],
  columnName: keyof T,
  multiplier: 1 | -1,
  numberGenerator: (item: unknown) => string = (item: unknown) => item as string,
): T[] => {
  return data.sort((a, b) => {
    const columnFromA = a[columnName];
    const columnFromB = b[columnName];

    return multiplier * comparePossibleRatios(columnFromA, columnFromB, numberGenerator);
  })
}

const comparePossibleRatios = <T>(
  columnFromA: T[keyof T],
  columnFromB: T[keyof T],
  numberGenerator: (item: unknown) => string = (item: unknown) => item as string,
): 1 | 0 | -1 => {
  if (columnFromA === columnFromB) {
    return 0;
  }

  // Handle nulls/non-strings to avoid null references later
  if (typeof columnFromA !== "string" && typeof columnFromB !== "string") {
    return 0;
  }
  if (typeof columnFromA !== "string") {
    return -1;
  }
  if (typeof columnFromB !== "string") {
    return 1;
  }

  const aParts = columnFromA.split(':');
  const bParts = columnFromB.split(':');

  // Handle non-ratios separately to avoid out-of-bounds problems later
  let aIsValid = aParts.length === 2;
  let bIsValid = bParts.length === 2;

  const invalidLengthResults = compareInvalidRatios(aIsValid, bIsValid, columnFromA, columnFromB);
  if (invalidLengthResults !== undefined) {
    return invalidLengthResults;
  }

  const aNumerator = parseNumber(numberGenerator(aParts[0]));
  const aDenominator = parseNumber(numberGenerator(aParts[1]));
  const bNumerator = parseNumber(numberGenerator(bParts[0]));
  const bDenominator = parseNumber(numberGenerator(bParts[1]));

  aIsValid = aIsValid && !isNaN(aNumerator) && !isNaN(aDenominator) && aDenominator !== 0;
  bIsValid = bIsValid && !isNaN(bNumerator) && !isNaN(bDenominator) && bDenominator !== 0;

  const invalidComparisonResults = compareInvalidRatios(aIsValid, bIsValid, columnFromA, columnFromB);
  if (invalidComparisonResults !== undefined) {
    return invalidComparisonResults;
  }

  return (aNumerator / aDenominator) > (bNumerator / bDenominator) ? 1 :
    (aNumerator / aDenominator) === (bNumerator / bDenominator) ? 0 : -1;
}

const compareInvalidRatios = (aIsValid: boolean, bIsValid: boolean, a: string, b: string): -1 | 0 | 1 | undefined => {
  if (!aIsValid && !bIsValid) {
    return a < b ? -1 : 1;
  }
  if (!aIsValid) {
    return -1;
  }
  if (!bIsValid) {
    return 1;
  }
  return undefined;
}

//this cleans the string of commas and other locale specific symbols so the string can be parsed
const parseNumber = (value: string, locale = navigator.language): number => {
  if (value === undefined || value === null) {
    return -Infinity;
  }
  if (typeof value === "number") {
    return value;
  }
  const example = Intl.NumberFormat(locale).format(1.1);
  const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, "g");
  const cleaned = value.replace(cleanPattern, "");
  const normalized = cleaned.replace(example.charAt(1), ".");

  return parseFloat(normalized);
};

const printNumberAsString = (input: number, fallback: string): string => {
  // We can't do a falsey check because 0 would fail, and we can't just do !isNaN because null returns false for isNaN
  if (input !== undefined && input !== null) {
    return input.toLocaleString();
  }
  return fallback;
};

interface SortingMetadata<T> {
  sortedColumn: keyof T;
  sortDirection: SortDirections;
}

interface Virtualization {
  rowHeight: number;
  viewportHeight: number;
}

interface WafflesPaginationProps {
  changePage: (pageNumber: number) => void;
  changeRecordsPerPage?: (pageNumber: number, recordsPerPage: number) => void;
  classNames?: string;
  enableBackendPagination?: boolean;
  handlePageSizeChange?: (pageSize: number) => void;
  initialPage?: number;
  pageSizeOptions?: number[];
  recordsPerPage: number;
  resetToFirstPage?: boolean;
  showPageSizeSelector?: boolean;
  skip?: number;
  totalRecords: number;
}

interface PaginationResult<T> {
  totalCount: number,
  items: T[]
}

enum WafflesThemes {
  Default = "default",
  // used in servicing provider flyout
  Flyout = "flyout",
  // used on dashboards
  Simple = "simple",
  // used to make body of pci dashboards scrollable
  Scrollable = "scrollable",
  // used in Provider Hub / Provider Profile / Access Point Details
  AccessPointDetails = "access-point-details",
  // used in Provider Hub / Contract Entity Profile / Organization Profile
  ContractEntityDetails = "contract-entity-details",
  // used in Compliance Connect dashboard widgets
  ComplianceConnect = "compliance-connect",
}

enum WafflesExpansionToggleStatus {
  Closed,
  Loading,
  Open,
}

export {
  ColumnGroup,
  ColumnConfig,
  comparePossibleRatios,
  numberSort,
  PaginationResult,
  parseNumber,
  printNumberAsString,
  SortingMetadata,
  SortDirections,
  TextAlignment,
  ratioSort,
  Virtualization,
  WafflesClassNames,
  type WafflesColumn,
  wafflesKeyColumnName,
  WafflesPaginationProps,
  WafflesExpansionToggleStatus,
  WafflesThemes,
};
